import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, Lock, Eye, EyeOff, Briefcase, Calendar } from 'lucide-react';
import { api } from '../../lib/api';

interface AddStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  restaurantId: string;
  editingStaff?: any;
}

interface RoleOption {
  value: string;
  label: string;
  description: string;
}

interface DepartmentOption {
  value: string;
  label: string;
}

const normalizeRoleCode = (value: string) => String(value || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
const ALLOWED_STAFF_ROLES = new Set(['MANAGER', 'KITCHEN_STAFF']);
const FALLBACK_ROLE_OPTIONS: RoleOption[] = [
  { value: 'MANAGER', label: 'Manager', description: 'Restaurant operations' },
  { value: 'KITCHEN_STAFF', label: 'Kitchen Staff', description: 'Kitchen operations' },
];
const FALLBACK_DEPARTMENT_OPTIONS: DepartmentOption[] = [
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'service', label: 'Service' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'management', label: 'Management' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'security', label: 'Security' },
];

const toRoleOption = (row: any): RoleOption | null => {
  const value = normalizeRoleCode(row?.code || row?.role_code || row?.name || '');
  if (!value) return null;

  return {
    value,
    label: row?.name || row?.role_name || row?.code || row?.role_code || value,
    description: row?.description || '',
  };
};

const toDepartmentRows = (response: any): any[] => {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.departments)) return raw.departments;
  if (Array.isArray(raw?.items)) return raw.items;
  return [];
};

export function AddStaffModal({ isOpen, onClose, onSuccess, restaurantId, editingStaff }: AddStaffModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: '',
    position: '',
    department: '',
    joiningDate: new Date().toISOString().split('T')[0],
    shift: 'morning',
    employeeId: '',
    isActive: true,
  });

  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<DepartmentOption[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    const loadMasterOptions = async () => {
      try {
        const [roleResponse, departmentResponse] = await Promise.all([
          api.masterData.userRoles.getAll(),
          api.masterData.departments.getAll({ isActive: true }),
        ]);

        const rows = Array.isArray(roleResponse?.data) ? roleResponse.data : [];
        const activeRoles = rows
          .filter((r: any) => (r?.isActive ?? r?.is_active) !== false)
          .map(toRoleOption)
          .filter((r): r is RoleOption => r !== null)
          .filter((r) => ALLOWED_STAFF_ROLES.has(normalizeRoleCode(r.value)));
        const activeDepartments = toDepartmentRows(departmentResponse)
          .filter((d: any) => (d?.isActive ?? d?.is_active) !== false)
          .map((d: any) => ({
            value: String(d.id || d.code || d.name || '').trim(),
            label: String(d.name || d.code || 'Department').trim(),
          }));

        if (!mounted) return;
        setRoleOptions(activeRoles.length ? activeRoles : FALLBACK_ROLE_OPTIONS);
        setDepartmentOptions(activeDepartments.length ? activeDepartments : FALLBACK_DEPARTMENT_OPTIONS);
      } catch {
        if (!mounted) return;
        setRoleOptions(FALLBACK_ROLE_OPTIONS);
        setDepartmentOptions(FALLBACK_DEPARTMENT_OPTIONS);
      }
    };

    loadMasterOptions();

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (editingStaff) {
      const editRole = normalizeRoleCode(editingStaff.role || editingStaff.roleName || '');
      setFormData({
        name: editingStaff.name || '',
        email: editingStaff.email || '',
        phone: editingStaff.phone || '',
        password: '',
        confirmPassword: '',
        role: editRole,
        position: editingStaff.position || '',
        department: editingStaff.departmentId || '',
        joiningDate: editingStaff.joiningDate || new Date().toISOString().split('T')[0],
        shift: editingStaff.shift || 'morning',
        employeeId: editingStaff.employeeId || '',
        isActive: editingStaff.isActive !== false,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role: '',
        position: '',
        department: '',
        joiningDate: new Date().toISOString().split('T')[0],
        shift: 'morning',
        employeeId: `EMP${Date.now().toString().slice(-6)}`,
        isActive: true,
      });
    }

    setErrors({});
  }, [isOpen, editingStaff]);

  useEffect(() => {
    if (!isOpen) return;
    if (formData.role) return;
    if (roleOptions.length === 0) return;

    setFormData((prev) => ({ ...prev, role: roleOptions[0].value }));
  }, [isOpen, roleOptions, formData.role]);
  useEffect(() => {
    if (!isOpen || !editingStaff || !departmentOptions.length) return;

    const current = String(formData.department || '').trim();
    const byCurrentValue = departmentOptions.find(
      (d) => d.value.toLowerCase() === current.toLowerCase()
    );
    if (byCurrentValue) return;

    const editingDepartmentId = String(editingStaff.departmentId || '').trim();
    const editingDepartmentName = String(
      editingStaff.department || editingStaff.departmentName || ''
    ).trim();

    const matched =
      (editingDepartmentId
        ? departmentOptions.find(
            (d) => d.value.toLowerCase() === editingDepartmentId.toLowerCase()
          )
        : undefined) ||
      (editingDepartmentName
        ? departmentOptions.find(
            (d) =>
              d.label.toLowerCase() === editingDepartmentName.toLowerCase() ||
              d.value.toLowerCase() === editingDepartmentName.toLowerCase()
          )
        : undefined);

    if (matched) {
      setFormData((prev) => ({ ...prev, department: matched.value }));
    }
  }, [isOpen, editingStaff, departmentOptions, formData.department]);
  const shifts = [
    { value: 'morning', label: 'Morning (6 AM - 2 PM)' },
    { value: 'afternoon', label: 'Afternoon (2 PM - 10 PM)' },
    { value: 'night', label: 'Night (10 PM - 6 AM)' },
    { value: 'full', label: 'Full Day (9 AM - 6 PM)' },
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';

    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) newErrors.phone = 'Invalid phone format';

    if (!formData.role) newErrors.role = 'Role is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.position.trim()) newErrors.position = 'Position is required';

    if (!editingStaff) {
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload: any = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        role: normalizeRoleCode(formData.role),
        departmentId: formData.department,
        joiningDate: formData.joiningDate || undefined,
        shift: formData.shift || undefined,
        isActive: formData.isActive,
      };

      if (editingStaff) {
        await api.users.update(editingStaff.id, payload);
        onSuccess('Staff member updated successfully');
      } else {
        payload.password = formData.password;
        payload.restaurantId = restaurantId;
        await api.users.create(payload);
        onSuccess('Staff member created successfully');
      }

      handleClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save staff member. Please try again.';
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-xl bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 text-white">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white/20 p-2">
              <User className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{editingStaff ? 'Edit' : 'Add'} Staff Member</h2>
              <p className="text-sm text-purple-100">Fill in the staff member details below</p>
            </div>
          </div>
          <button type="button" onClick={handleClose} className="rounded-lg p-2 transition-colors hover:bg-white/20">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {errors.submit && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{errors.submit}</div>}

          <div>
            <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
              <User className="h-5 w-5 text-purple-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="Enter full name"
                />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Email *</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full rounded-lg border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="Enter email"
                  />
                </div>
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Phone *</label>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={`w-full rounded-lg border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.phone ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="Enter phone number"
                  />
                </div>
                {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.role ? 'border-red-300' : 'border-gray-300'}`}
                >
                  <option value="">Select role</option>
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.role && <p className="mt-1 text-xs text-red-600">{errors.role}</p>}
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
              <Briefcase className="h-5 w-5 text-purple-600" />
              Work Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Position *</label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.position ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="e.g. Kitchen Staff"
                />
                {errors.position && <p className="mt-1 text-xs text-red-600">{errors.position}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Department *</label>
                <select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.department ? 'border-red-300' : 'border-gray-300'}`}
                >
                  <option value="">Select department</option>
                  {departmentOptions.map((department) => (
                    <option key={department.value} value={department.value}>
                      {department.label}
                    </option>
                  ))}
                </select>
                {errors.department && <p className="mt-1 text-xs text-red-600">{errors.department}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Joining Date</label>
                <div className="relative">
                  <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={formData.joiningDate}
                    onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Shift</label>
                <select
                  value={formData.shift}
                  onChange={(e) => setFormData({ ...formData, shift: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {shifts.map((shift) => (
                    <option key={shift.value} value={shift.value}>
                      {shift.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {!editingStaff && (
            <div>
              <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                <Lock className="h-5 w-5 text-purple-600" />
                Account Security
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`w-full rounded-lg border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Confirm Password *</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`w-full rounded-lg border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>}
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500"
                aria-label="Active Employee"
              />
              <span className="font-medium text-gray-700 select-none">Active Employee</span>
            </div>
          </div>

          <div className="flex gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 py-3 font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  {editingStaff ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  <User className="h-5 w-5" />
                  {editingStaff ? 'Update Staff' : 'Add Staff'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
